/**
 * The bot a player fights: offline quick fights (M1) and the matchmaking bot fill (M2) use the
 * same rules, so a bot is the same opponent either way.
 */
import { BOT_NAMES, BOT_RANK_SCALE, type BotDifficulty } from '../config/bot';
import { fighterPrice } from '../config/cosmetics';
import { ROSTER } from '../config/roster';
import { emptyRanks, type SkillRanks } from '../config/skills';

export interface BotOpponent {
  name: string;
  fighterId: string;
  ranks: SkillRanks;
  level: number;
}

/** A side of a match, as far as telling the two fighters apart goes. */
export interface FighterPick {
  fighterId: string;
  /** No account yet. */
  guest: boolean;
}

/**
 * Two players on the same fighter would look alike. When a guest is one of them, the guest fights
 * as another free fighter this time (two guests: the second one; D52: never a paid one they don't
 * own), picked from the seed; a registered player keeps the fighter they chose (D43). Returns both
 * sides' fighters.
 */
export function distinctFighters(
  a: FighterPick,
  b: FighterPick,
  seed: number,
  roster: readonly string[] = ROSTER,
): [string, string] {
  if (a.fighterId !== b.fighterId || (!a.guest && !b.guest)) return [a.fighterId, b.fighterId];
  const others = roster.filter((id) => id !== a.fighterId && !fighterPrice(id));
  const other = others[(seed >>> 8) % Math.max(1, others.length)];
  if (!other) return [a.fighterId, b.fighterId];
  return b.guest ? [a.fighterId, other] : [other, b.fighterId];
}

export function botOpponent(
  player: { fighterId: string; ranks: SkillRanks; level: number },
  difficulty: BotDifficulty,
  seed: number,
  roster: readonly string[] = ROSTER,
): BotOpponent {
  const ranks = emptyRanks();
  const scale = BOT_RANK_SCALE[difficulty];
  for (const k of Object.keys(ranks) as (keyof SkillRanks)[]) ranks[k] = Math.floor(player.ranks[k] * scale);
  const s = seed >>> 0;
  // Any other fighter, so the two never look alike.
  const others = roster.filter((id) => id !== player.fighterId);
  return {
    name: BOT_NAMES[s % BOT_NAMES.length] ?? 'Pebble',
    fighterId: others[(s >>> 8) % Math.max(1, others.length)] ?? player.fighterId,
    ranks,
    level: Math.max(1, player.level + ((s >> 4) % 5) - 2),
  };
}
